<template>
  <div class="flex flex-col gap-6">
    <!-- Loading State -->
    <div v-if="loading" class="flex flex-col items-center justify-center p-12 gap-2 text-gray-500">
      <theme-icon icon="loading" type="icons" classes="w-8 h-8 animate-spin" />
      <p>Loading permissions...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="flex items-center gap-3 p-4 bg-red-50 border-l-4 border-red-500 rounded text-red-600">
      <theme-icon icon="alert-circle" type="icons" classes="w-5 h-5" />
      <p>{{ error }}</p>
    </div>

    <!-- Global Permissions Section -->
    <themed-card v-if="!loading && globalPermissionTypes.length > 0" class="pt-6">
      <div class="mb-4">
        <h3 class="text-lg font-medium m-0 mb-1">Global Permissions</h3>
        <p class="text-sm text-gray-600 m-0">These permissions apply across all sites</p>
      </div>
      <div class="flex flex-wrap gap-4">
        <themed-checkbox
          v-for="permission in globalPermissionTypes"
          :key="permission.id"
          :id="`global-${permission.name}`"
          :label="permission.display_name || permission.name"
          :checked="hasGlobalPermission(permission.name)"
          @change="updateGlobalPermission(permission.name, $event)"
          :disabled="saving"
        />
      </div>
    </themed-card>

    <!-- Entity-Level Permissions Table -->
    <themed-card v-if="!loading && !error" class="overflow-hidden pt-6">
      <div class="mb-4">
        <h3 class="text-lg font-medium m-0 mb-1">Site-Specific Permissions</h3>
        <p class="text-sm text-gray-600 m-0">These permissions apply to specific sites</p>
      </div>

      <div class="flex gap-3 items-center justify-end mb-4 flex-wrap">
        <themed-select
          v-if="clientOptions.length > 1"
          v-model="selectedClientFilter"
          :options="clientOptions"
          class="min-w-[200px] max-w-[250px]"
        />
        <themed-search
          v-model="searchQuery"
          placeholder="Search sites..."
          class="flex-1 min-w-[250px] max-w-[250px]"
        />
      </div>

      <themed-table
        :columns="tableColumns"
        :data="filteredSites"
        :loading="loading"
        empty-message="No sites found"
        :pagination="true"
        :per-page="perPage"
      >
        <template #column-name="{ row }">
          <div class="flex flex-col gap-1">
            <div class="font-medium">{{ row.name }}</div>
            <div v-if="row.building" class="text-sm text-gray-600">
              {{ row.building.address_line_1 }}, {{ row.building.city }}, {{ row.building.state }} {{ row.building.zip_code }}
            </div>
          </div>
        </template>

        <template #column-client="{ row }">
          <div class="text-sm text-gray-600">
            {{ row.client ? row.client.name : 'N/A' }}
          </div>
        </template>

        <template v-for="permission in entityLevelPermissionTypes" v-slot:[`column-${permission.name}`]="{ row }">
          <div :key="permission.id" class="flex justify-center items-center">
            <themed-checkbox
              :checked="hasPermission(row.id, permission.name)"
              @change="updatePermission(row, permission.name, $event)"
            />
          </div>
        </template>
      </themed-table>
    </themed-card>
  </div>
</template>

<script>
import ThemedCard from '../../ThemedCard.vue';
import ThemedSearch from '../../ThemedSearch.vue';
import ThemedCheckbox from '../../ThemedCheckbox.vue';
import ThemedTable from '../../ThemedTable.vue';
import ThemedSelect from '../../ThemedSelect.vue';
import ThemeIcon from '../../ThemeIcon.vue';

export default {
  name: 'SitePermissions',

  components: {
    ThemedCard,
    ThemedSearch,
    ThemedCheckbox,
    ThemedTable,
    ThemedSelect,
    ThemeIcon
  },

  props: {
    userId: {
      type: [Number, String],
      required: true
    },
    clientId: {
      type: [Number, String],
      required: false,
      default: null
    }
  },

  data() {
    return {
      sites: [],
      permissionTypes: [],
      loading: false,
      saving: false,
      error: null,
      searchQuery: '',
      selectedClientFilter: '',
      permissionGroup: 'site',
      userPermissions: {},
      globalPermissions: [],
      originalPermissions: {}, // Track original state for change detection
      hasChanges: false,
      perPage: 10
    };
  },

  computed: {
    clientOptions() {
      if (!this.sites || !Array.isArray(this.sites)) {
        return [{ value: '', label: 'All Clients' }];
      }
      
      const clientMap = new Map();
      
      this.sites.forEach(site => {
        if (site.client && !clientMap.has(site.client.id)) {
          clientMap.set(site.client.id, {
            value: site.client.id,
            label: site.client.name
          });
        }
      });
      
      return [
        { value: '', label: 'All Clients' },
        ...Array.from(clientMap.values()).sort((a, b) => a.label.localeCompare(b.label))
      ];
    },

    filteredSites() {
      if (!this.sites || !Array.isArray(this.sites)) {
        return [];
      }
      
      let filtered = this.sites;
      
      // Filter by client if selected
      if (this.selectedClientFilter) {
        filtered = filtered.filter(site => 
          site.client && site.client.id === this.selectedClientFilter
        );
      }
      
      // Filter by search query
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(site =>
          site.name.toLowerCase().includes(query) ||
          (site.building?.address_line_1 || '').toLowerCase().includes(query) ||
          (site.building?.city || '').toLowerCase().includes(query) ||
          (site.building?.state || '').toLowerCase().includes(query) ||
          (site.building?.zip_code || '').toLowerCase().includes(query) ||
          (site.client?.name || '').toLowerCase().includes(query)
        );
      }
      
      return filtered;
    },

    globalPermissionTypes() {
      return this.permissionTypes.filter(pt => pt.is_global === true);
    },

    entityLevelPermissionTypes() {
      return this.permissionTypes.filter(pt => pt.is_global === false);
    },

    tableColumns() {
      const columns = [
        { field: 'name', label: 'Site', sortable: true },
        { field: 'client', label: 'Client', sortable: true }
      ];

      this.entityLevelPermissionTypes.forEach(permission => {
        columns.push({
          field: permission.name,
          label: permission.display_name || permission.name,
          sortable: false,
          align: 'center'
        });
      });

      return columns;
    }
  },

  watch: {
    userId: {
      immediate: false,
      handler(newUserId, oldUserId) {
        if (newUserId && newUserId !== oldUserId && newUserId !== 'new') {
          this.fetchData();
        }
      }
    },
    clientId: {
      immediate: false,
      handler(newClientId, oldClientId) {
        if (newClientId !== oldClientId) {
          this.fetchData();
        }
      }
    }
  },

  created() {
    this.fetchData();
    
    // Listen for save events from parent
    this.$eventHub.$on('save-user-site-permissions', this.saveAllPermissions);
    this.$eventHub.$on('save-all-permissions', this.saveAllPermissions);
    
    // Listen for client permission changes (for broker users)
    this.$eventHub.$on('reload-project-site-permissions', this.fetchData);
  },

  beforeDestroy() {
    this.$eventHub.$off('save-user-site-permissions', this.saveAllPermissions);
    this.$eventHub.$off('save-all-permissions', this.saveAllPermissions);
    this.$eventHub.$off('reload-project-site-permissions', this.fetchData);
  },

  methods: {
    hasPermission(siteId, permissionName) {
      return this.userPermissions[siteId] && Array.isArray(this.userPermissions[siteId])
        ? this.userPermissions[siteId].includes(permissionName)
        : false;
    },

    hasGlobalPermission(permissionName) {
      return this.globalPermissions.includes(permissionName);
    },

    async fetchData() {
      this.loading = true;
      this.error = null;

      try {
        // Only fetch permissions - the API returns sites with permission states
        const permissionsUrl = this.userId 
          ? `/api/users/${this.userId}/permissions/${this.permissionGroup}`
          : null;

        if (!permissionsUrl) {
          this.loading = false;
          return;
        }

        const permissionsRes = await axios.get(permissionsUrl);

        const responseData = permissionsRes.data?.data || {};
        const items = responseData.items || [];
        const groupData = responseData.group || {};

        this.permissionTypes = groupData.permission_types || [];

        // Extract sites from items (excluding global permissions item)
        this.sites = items.filter(item => item.id !== null);

        this.userPermissions = {};
        this.globalPermissions = [];

        if (Array.isArray(items)) {
          items.forEach(item => {
            const sitePermissions = [];

            if (item.permissions && typeof item.permissions === 'object') {
              Object.entries(item.permissions).forEach(([permName, hasPerm]) => {
                if (hasPerm) {
                  if (item.id === null) {
                    this.globalPermissions.push(permName);
                  } else {
                    sitePermissions.push(permName);
                  }
                }
              });
            }

            if (item.id !== null) {
              this.$set(this.userPermissions, item.id, sitePermissions);
            }
          });
        }
        
        // Save original state for change tracking
        this.saveOriginalState();

      } catch (error) {
        console.error('Error fetching site permissions:', error);
        this.error = 'Failed to load site permissions. Please try again later.';
      } finally {
        this.loading = false;
      }
    },

    updatePermission(site, permissionName, hasPermission) {
      if (!this.userPermissions[site.id]) {
        this.$set(this.userPermissions, site.id, []);
      }

      if (hasPermission) {
        if (!this.userPermissions[site.id].includes(permissionName)) {
          this.userPermissions[site.id].push(permissionName);
        }
      } else {
        const index = this.userPermissions[site.id].indexOf(permissionName);
        if (index > -1) {
          this.userPermissions[site.id].splice(index, 1);
        }
      }
      
      this.checkForChanges();
    },

    updateGlobalPermission(permissionName, hasPermission) {
      if (hasPermission) {
        if (!this.globalPermissions.includes(permissionName)) {
          this.globalPermissions.push(permissionName);
        }
      } else {
        const index = this.globalPermissions.indexOf(permissionName);
        if (index > -1) {
          this.globalPermissions.splice(index, 1);
        }
      }
      
      this.checkForChanges();
    },
    
    checkForChanges() {
      const currentState = JSON.stringify({
        user: this.userPermissions,
        global: this.globalPermissions
      });
      const originalState = JSON.stringify(this.originalPermissions);
      this.hasChanges = currentState !== originalState;
    },
    
    saveOriginalState() {
      this.originalPermissions = {
        user: JSON.parse(JSON.stringify(this.userPermissions)),
        global: [...this.globalPermissions]
      };
      this.hasChanges = false;
    },

    async saveAllPermissions() {
      this.saving = true;
      this.error = null;

      try {
        const promises = [];

        // Only save permissions for sites that have changed
        Object.entries(this.userPermissions).forEach(([siteId, permissions]) => {
          const site = this.sites.find(s => s.id == siteId);
          if (site) {
            // Check if permissions changed for this site
            const originalPerms = (this.originalPermissions.user && this.originalPermissions.user[siteId]) || [];
            const currentSorted = [...permissions].sort();
            const originalSorted = [...originalPerms].sort();
            const hasChanged = JSON.stringify(currentSorted) !== JSON.stringify(originalSorted);
            
            if (hasChanged) {
              const permissionObject = {};
              this.entityLevelPermissionTypes.forEach(type => {
                permissionObject[type.name] = permissions.includes(type.name);
              });

              promises.push(
                axios.post(`/api/users/${this.userId}/permissions`, {
                  permissionable_type: 'site',
                  permissionable_id: siteId,
                  permissions: permissionObject
                })
              );
            }
          }
        });

        // Check if global permissions changed
        const currentGlobalSorted = [...this.globalPermissions].sort();
        const originalGlobalSorted = this.originalPermissions.global ? [...this.originalPermissions.global].sort() : [];
        const globalChanged = JSON.stringify(currentGlobalSorted) !== JSON.stringify(originalGlobalSorted);
        
        if (globalChanged) {
          const globalPermissionObject = {};
          this.globalPermissionTypes.forEach(type => {
            globalPermissionObject[type.name] = this.globalPermissions.includes(type.name);
          });

          promises.push(
            axios.post(`/api/users/${this.userId}/permissions`, {
              permissionable_type: 'site',
              permissionable_id: null,
              permissions: globalPermissionObject
            })
          );
        }

        if (promises.length === 0) {
          this.saving = false;
          this.$eventHub.$emit('permission-saved', { section: 'Site Permissions', hasChanges: false });
          return;
        }

        await Promise.all(promises);
        
        // Update original state after successful save
        this.saveOriginalState();
        
        this.$eventHub.$emit('permission-saved', { section: 'Site Permissions', hasChanges: true });

      } catch (error) {
        console.error('Error saving site permissions:', error);
        this.error = 'Failed to save site permissions. Please try again.';
        
        this.$eventHub.$emit('permission-save-error');
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>
